/**
 * WHAT THE SHELL NEEDS FROM A SEASON RACE THAT THE ENGINE DOES NOT HAND BACK.
 *
 * Two pure functions, both of them plumbing, both of them tested against the
 * engine's own output rather than against a fixture:
 *
 *   • `seasonStartingGrid` — the RaceState as the field lined up, which the race
 *     tape needs for cursor 0 and for the first turn's movement deltas.
 *     `RaceResult` carries the FINAL state and every turn's state, but not the
 *     standing start, so the shell rebuilds it the way `runSeasonRace` built it.
 *   • `settleSeason` — the money and the winter over a finished `SeasonResult`,
 *     which is what turns a race-by-race season into the `CareerSeason` that
 *     Save and the record book take.
 *
 * NEITHER IS A RULE, and neither may become one. If something here starts
 * deciding what happened rather than restating it, it belongs in `src/domain`.
 */

import { buildStartingGrid, type RaceSetup as EngineRaceSetup } from '../domain/race.ts';
import type { RaceState, DriverCard, RaceGrants } from '../domain/types.ts';
import type { SeasonHouseRules, SeasonRaceStep, SeasonState } from '../domain/seasonPlay.ts';
import { grantsAreEmpty } from '../domain/interviewEffects.ts';
import type { SeasonResult } from '../domain/season.ts';
import type { Team } from '../domain/field.ts';
import { runOffseason } from '../domain/offseason.ts';
import {
  ownerMap,
  runSeasonFinance,
  seasonFinanceReport,
  teamMap,
} from '../domain/finance/seasonFinance.ts';
import { profileFromCard } from '../domain/finance/raceFinance.ts';
import { foldMoney } from '../domain/finance/money.ts';
import type { CareerSeason } from '../lib/careerRunner.ts';
import type { CareerCarry } from '../domain/careerCarry.ts';
import type { HouseRuleOptions } from '../domain/houseRules.ts';

/**
 * THE FIELD AS IT LINED UP for a season race that has already been run.
 *
 * ⚠ THIS IS A RECONSTRUCTION, and the four inputs it uses are the same four
 * `runSeasonRace` used, taken from the same two objects:
 *
 *   drivers   `step.week.grid`, in grid order (index 0 is the pole), with any
 *             card a POST RACE INTERVIEW rewrote for this race swapped in
 *             (D-EZ-15). `startPositions` is built from exactly this list, so
 *             the test asserts the two agree driver for driver.
 *   chips     race week's own qualifying chips, plus the chips last week's
 *             interview handed this driver for this race only.
 *   grants    the interview's "when" effects, which ride on the car.
 *   track /   the calendar's, off `step.result.scheduled`.
 *   distance
 *
 * `before` is the season state as it stood BEFORE the race — that is where the
 * owed interview lives, because `runSeasonRace` empties it as it spends it.
 * Passing the state AFTER the race would silently drop the chip and the card
 * edit, which is why the argument is named for the season it belongs to.
 *
 * If `runSeasonRace` ever changes how it builds its setup, `seasonPlayModel.test.ts`
 * fails: it compares this grid against the cards the race actually ran.
 */
export function seasonStartingGrid(before: SeasonState, step: SeasonRaceStep): RaceState {
  const owed = new Map(before.carry.interview);

  const drivers: DriverCard[] = step.week.grid.map((card) => owed.get(card.driverId)?.card ?? card);

  const startingChips: Record<string, number> = { ...step.week.chips };
  const grants: Record<string, RaceGrants> = {};
  for (const [driverId, effect] of owed) {
    // Only a driver who is actually in this field: an interview owed to a man
    // who did not qualify is spent by the engine and lands on nobody.
    if (!step.week.grid.some((c) => c.driverId === driverId)) continue;
    if (effect.chips > 0) startingChips[driverId] = (startingChips[driverId] ?? 0) + effect.chips;
    if (!grantsAreEmpty(effect.grants)) grants[driverId] = effect.grants;
  }

  const setup: EngineRaceSetup = {
    track: step.race.track,
    drivers,
    seed: step.result.raceSeed,
    totalTurns: step.race.scheduledTurns,
    raceName: step.result.scheduled.raceName,
    tvList: step.week.tvList,
    feuds: step.week.feuds,
    fuming: step.week.fuming,
    startingChips,
    grants,
  };
  return buildStartingGrid(setup);
}

export interface SettleInput {
  seed: number;
  seasonNumber: number;
  /** The roster the season was RUN with — not the re-rated one. */
  roster: DriverCard[];
  teams: Team[];
  season: SeasonResult;
  /** Carried out of the previous winter, when there was one. */
  seasonsRun?: Record<string, number>;
  /**
   * D-EZ-91 / D-EZ-94 — the rest of what the previous winter handed on. MUST be
   * the same carry the season was begun with (`beginSeason({ carryIn })` and
   * the finance track's `carry`), or the settled books open on different
   * sponsors than the weekends were paid on.
   */
  carry?: CareerCarry;
  /**
   * D-EZ-103 — the rules the season was raced and scored under, recorded on the
   * settled season so the league's settings carry into the next one (and a
   * loaded league knows them). Pass `state.options.race` / `.houseRules`.
   */
  raceRules?: HouseRuleOptions;
  houseRules?: SeasonHouseRules;
}

/**
 * THE BOOKS AND THE WINTER, over a season that has already been raced.
 *
 * These are the last three steps of `lib/careerRunner.ts`'s `runCareerSeason`,
 * in the same order, with the same arguments — finance reads the season, the
 * off-season reads finance, and the report reads both. `runCareerSeason` cannot
 * be reused directly because it RUNS the thirty-six races itself, and a season
 * played one race at a time has already run them; calling it would race the year
 * a second time and hand back a different one.
 *
 * ⚠ KEEP IN STEP WITH `runCareerSeason`. Two callers building a `CareerSeason`
 * two different ways is exactly the drift this project keeps out of the engine,
 * so this function does nothing of its own: no defaults it invents, no options
 * it chooses, no ledger it edits.
 */
export function settleSeason(input: SettleInput): CareerSeason {
  const { seed, seasonNumber, roster, teams, season } = input;
  const carry = input.carry ?? season.inherited ?? undefined;

  const teamOf = teamMap(roster);
  const finance = runSeasonFinance({
    season,
    roster,
    teamOf,
    options: { ownerOf: ownerMap(teams) },
    ...(carry ? { carry: carry.finance } : {}),
  });

  const offseason = runOffseason({
    seed,
    season,
    roster,
    teams,
    money: finance.events,
    financeTeamOf: teamOf,
    seasonsRun: input.seasonsRun,
    options: { seasonNumber },
    financeCarry: finance.carry,
    priors: carry?.priors,
  });

  const profiles = roster.map((c) => profileFromCard(c, teamOf[c.driverId]));

  return {
    seasonNumber,
    seed,
    roster,
    teams,
    season,
    finance,
    offseason,
    report: seasonFinanceReport(finance, profiles),
    ledger: foldMoney(finance.events),
    teamOf,
    ...(input.houseRules && Object.keys(input.houseRules).length ? { houseRules: input.houseRules } : {}),
    ...(input.raceRules && Object.keys(input.raceRules).length ? { raceRules: input.raceRules } : {}),
  };
}
